import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../api';
import { Spinner, Toast, css, PageHeader, Modal, Label, Grid } from '../components/UI';

// Catálogo de cuentas bancarias de cada compañía (backend: models/CuentaBancaria.js).
// Lo administran admin y finanzas. Tesorería elige una de estas cuentas al marcar
// un pago como pagado y la póliza contable toma de aquí la cuenta del banco.
const VACIA = { empresa: '', banco: '', terminacion: '', clabe: '', numeroCuenta: '', cuentaContable: '', principal: false, activa: true };

export default function CuentasBancarias() {
  const [cuentas, setCuentas] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [filtro, setFiltro] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [form, setForm] = useState(null); // { ...cuenta, _id? , guardando }
  const [toast, setToast] = useState(null);
  const toast_ = (msg, tipo = 'ok') => { setToast({ msg, tipo }); setTimeout(() => setToast(null), 4000); };

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try { setCuentas(await api.get('/cuentas-bancarias?todas=1')); }
    catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); api.get('/empresas').then(l => setEmpresas(l.map(e => e.nombre))).catch(() => {}); }, [load]);

  const guardar = async () => {
    if (!form.empresa) { toast_('Elige la compañía', 'error'); return; }
    if (!form.banco.trim()) { toast_('Escribe el banco', 'error'); return; }
    setForm(f => ({ ...f, guardando: true }));
    try {
      if (form._id) await api.put(`/cuentas-bancarias/${form._id}`, form);
      else await api.post('/cuentas-bancarias', form);
      toast_(form._id ? 'Cuenta actualizada' : 'Cuenta agregada');
      setForm(null); load();
    } catch (e) { toast_(e.message, 'error'); setForm(f => ({ ...f, guardando: false })); }
  };
  const alternarActiva = async (c) => {
    try { await api.put(`/cuentas-bancarias/${c._id}`, { ...c, activa: !c.activa }); toast_(c.activa ? 'Cuenta desactivada' : 'Cuenta activada'); load(); }
    catch (e) { toast_(e.message, 'error'); }
  };

  const visibles = cuentas.filter(c => !filtro || c.empresa === filtro);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const th = { padding: '9px 12px', textAlign: 'left', fontWeight: 600 };
  const td = { padding: '9px 12px', borderTop: '1px solid var(--mesh-soft)' };

  return (
    <div>
      <Toast msg={toast?.msg} tipo={toast?.tipo} onClose={() => setToast(null)} />
      <PageHeader title="Cuentas Bancarias"
        subtitle="Las cuentas de cada compañía de las que salen los pagos. Tesorería elige una al marcar un pago como pagado y la póliza contable toma de aquí la cuenta del banco."
        actions={<>
          <select value={filtro} onChange={e => setFiltro(e.target.value)} style={{ ...css.select(false), minWidth: 220 }} aria-label="Filtrar por compañía">
            <option value="">Todas las compañías</option>
            {empresas.map(e => <option key={e} value={e}>{e}</option>)}
          </select>
          <button type="button" onClick={() => setForm({ ...VACIA, empresa: filtro })} style={css.btn('primario')}>+ Agregar cuenta</button>
        </>} />
      {loading && !cuentas.length && <Spinner />}
      {error && <div role="alert" style={{ ...css.card, padding: 16, color: 'var(--danger)' }}>{error}</div>}
      {!loading && !error && (
        <div style={{ ...css.card, padding: 0, overflowX: 'auto' }}>
          {visibles.length ? (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 760 }}>
              <thead><tr>{['Compañía', 'Banco', 'Cuenta', 'CLABE', 'Cuenta contable', ''].map((h, i) => <th key={i} scope="col" style={th}>{h}</th>)}</tr></thead>
              <tbody>
                {visibles.map(c => (
                  <tr key={c._id} style={{ opacity: c.activa ? 1 : 0.55 }}>
                    <td style={td}>{c.empresa}</td>
                    <td style={td}><strong>{c.banco}</strong>{c.principal && <span style={{ fontSize: 11.5, color: 'var(--plasma)', marginLeft: 6 }}>principal</span>}{!c.activa && <span style={{ fontSize: 11.5, color: 'var(--glass)', marginLeft: 6 }}>desactivada</span>}</td>
                    <td style={{ ...td, fontFamily: 'var(--digit-font)' }}>{c.numeroCuenta || (c.terminacion ? `••${c.terminacion}` : '—')}</td>
                    <td style={{ ...td, fontFamily: 'var(--digit-font)', fontSize: 12 }}>{c.clabe || '—'}</td>
                    <td style={{ ...td, fontFamily: 'var(--digit-font)' }}>{c.cuentaContable || <span style={{ color: 'var(--danger)', fontFamily: 'inherit' }}>Falta</span>}</td>
                    <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <button type="button" onClick={() => setForm({ ...VACIA, ...c })} style={{ ...css.btnGhost, fontSize: 12, padding: '4px 10px', marginRight: 6 }}>Editar</button>
                      <button type="button" onClick={() => alternarActiva(c)} style={{ ...css.btnGhost, fontSize: 12, padding: '4px 10px' }}>{c.activa ? 'Desactivar' : 'Activar'}</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div style={{ padding: 18, fontSize: 13, color: 'var(--glass)' }}>
              {filtro ? `${filtro} no tiene cuentas dadas de alta.` : 'Aún no hay cuentas dadas de alta.'} Mientras una compañía no tenga cuentas, sus pagos se registran sin cuenta, como hasta ahora.
            </div>
          )}
        </div>
      )}

      <Modal open={!!form} onClose={() => !form?.guardando && setForm(null)} title={form?._id ? 'Editar cuenta' : 'Agregar cuenta'} width={520}>
        {form && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div>
              <Label required>Compañía</Label>
              <select value={form.empresa} onChange={e => set('empresa', e.target.value)} style={css.select(!form.empresa)}>
                <option value="">Elige la compañía…</option>
                {empresas.map(e => <option key={e} value={e}>{e}</option>)}
              </select>
            </div>
            <Grid cols={2} gap={10}>
              <div><Label required>Banco</Label><input value={form.banco} onChange={e => set('banco', e.target.value)} placeholder="BBVA" style={css.input(!form.banco.trim())} /></div>
              <div><Label>Últimos 4 dígitos</Label><input value={form.terminacion} onChange={e => set('terminacion', e.target.value.replace(/\D/g, '').slice(0, 4))} inputMode="numeric" placeholder="1234" style={css.input(false)} /></div>
            </Grid>
            <div>
              <Label>Número de cuenta</Label>
              <input value={form.numeroCuenta} onChange={e => set('numeroCuenta', e.target.value.replace(/D/g, '').slice(0, 11))} inputMode="numeric" placeholder="Ej. 0178893284 (sale de la CLABE si lo dejas vacío)" style={css.input(false)} />
              <div style={{ fontSize: 11.5, color: 'var(--glass)', marginTop: 4 }}>Es la "Cuenta de retiro" de los comprobantes del banco: con ella la carga masiva reconoce de qué cuenta salió cada pago.</div>
            </div>
            <div><Label>CLABE</Label><input value={form.clabe} onChange={e => set('clabe', e.target.value.replace(/\D/g, '').slice(0, 18))} inputMode="numeric" placeholder="18 dígitos (opcional)" style={css.input(form.clabe && form.clabe.length !== 18)} /></div>
            <div>
              <Label>Cuenta contable</Label>
              <input value={form.cuentaContable} onChange={e => set('cuentaContable', e.target.value)} placeholder="1120-001-002" style={css.input(false)} />
              <div style={{ fontSize: 11.5, color: 'var(--glass)', marginTop: 4 }}>La cuenta de bancos en Aspel. Si falta, contabilidad la completa después; la póliza la marca como pendiente.</div>
            </div>
            <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
              <input type="checkbox" checked={form.principal} onChange={e => set('principal', e.target.checked)} />
              Cuenta principal de la compañía (viene elegida al pagar y se usa en las pólizas de los pagos anteriores a este catálogo)
            </label>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => setForm(null)} disabled={form.guardando} style={css.btnGhost}>Cancelar</button>
              <button type="button" onClick={guardar} disabled={form.guardando} style={css.btn('primario')}>{form.guardando ? 'Guardando…' : 'Guardar'}</button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
